// src/services/trainingService.js - Frontend service for training functionality
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

// Helper function to get token
const getToken = () => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.token;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }
  return null;
};

const api = axios.create({
  baseURL: API_URL
});

// Add auth token to all requests
api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const trainingService = {
  /**
   * Start manual training upload (production-safe)
   * @param {File} file
   * @returns {Object}
   */
  async startManualTrainingUpload(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/training', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error starting manual training upload:', error);
      throw error;
    }
  },

  /**
   * Save manually placed fields as training examples with context
   * @param {string} documentId
   * @param {Array} fields - [{ type, page, x, y, width, height }]
   * @param {Object} documentMetadata
   */
  async saveManualFields(documentId, fields, documentMetadata = {}) {
    try {
      const payload = { documentId, fields, documentMetadata };
      const response = await api.post('/training/save-fields', payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error saving manual training fields:', error);
      throw error;
    }
  },
  /**
   * Upload document for training
   * @param {string} documentId - Document ID
   * @param {Object} options - Training options
   * @returns {Object} - Training results
   */
  async uploadDocumentForTraining(documentId, options = {}) {
    try {
      console.log(`📚 Uploading document for training: ${documentId}`);

      const formData = new FormData();
      formData.append('mode', options.mode || 'auto');
      formData.append('metadata', JSON.stringify(options.metadata || {}));

      // For now, we'll simulate the upload since we don't have the actual file
      // In a real implementation, you'd need to pass the file here
      const response = await api.post('/training/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ Training upload completed:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Error uploading document for training:', error);
      throw error;
    }
  },

  /**
   * Get training documents for current user
   * @returns {Array} - Training documents
   */
  async getTrainingDocuments() {
    try {
      console.log('📚 Fetching training documents');

      const response = await api.get('/training/documents');
      console.log('✅ Training documents fetched:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Error fetching training documents:', error);
      throw error;
    }
  },

  /**
   * Update training data with corrections
   * @param {Object} trainingData - Training corrections
   * @returns {Object} - Update results
   */
  async updateTrainingData(trainingData) {
    try {
      console.log('🧠 Updating training data');

      const response = await api.post('/training/update', trainingData);
      console.log('✅ Training data updated:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Error updating training data:', error);
      throw error;
    }
  },

  /**
   * Get training statistics
   * @returns {Object} - Training statistics
   */
  async getTrainingStats() {
    try {
      console.log('📊 Fetching training statistics');

      const response = await api.get('/training/stats');
      console.log('✅ Training statistics fetched:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Error fetching training statistics:', error);
      throw error;
    }
  },

  /**
   * Upload a file for training
   * @param {File} file - File to upload
   * @param {Object} options - Upload options
   * @returns {Object} - Upload results
   */
  async uploadFileForTraining(file, options = {}) {
    try {
      console.log(`📚 Uploading file for training: ${file.name}`);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', options.mode || 'auto');
      formData.append('metadata', JSON.stringify(options.metadata || {}));

      const response = await api.post('/training/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ File upload for training completed:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Error uploading file for training:', error);
      throw error;
    }
  }
};

export default trainingService;